import React from 'react';
import { useTitle } from "fearless-common-ui";
import {useParams} from "react-router";
import useGet from "../../../components/hooks/useGet";
import {backendEndpoints} from "../../../libs/backendEndpoints";
import {Alert} from "react-bootstrap";
import {routes} from "../../../utils";
import useAuth from "../../../components/hooks/useAuth";
import {ArrowRight} from "react-bootstrap-icons";
import ShareableLinkPage from "../ShareableLinkPage";
import SelectStudentGroupToJoin, {CourseWithData} from "./SelectStudentGroupToJoin";
import Loading from "../../../components/Loading";
import RedirectPage from "../../RedirectPage";
import Pages from "../../../components/layout/Pages";


/**
 * In the platform, users can share links which allow anyone to join a group using a secure URL.
 */
export default function JoinCourseLink() {
  const {user, isLoading: loadingUser} = useAuth();
  const {courseId, hash} = useParams() as { courseId: string, hash: string };

  useTitle(`Join Course`);

  const {data: course, isLoading, error} = useGet<CourseWithData>({
    endpoint: courseId ? backendEndpoints.getCourseViaHash(courseId, hash) : '',
    params: {_with: ['groups.users', 'group_clusters']},
  });

  const thisPageUrl = routes.coursesJoinLink(courseId, hash);
  const alreadyInCourse = course?.groups
    .map(g => g.users)
    .flat()
    .find((u) => u.id === user?.id);

  if (user) {
    return (
      <ShareableLinkPage
        tabTitle={`Join course ${course?.name || ''}`}
        header={`Join the course`}
        description={(descProps) => <span>You have been invited by <b>{descProps.user?.first_name}</b> to join.</span>}
        currentPageUrl={thisPageUrl}
        isLoading={isLoading}
        error={error}
        course={course}
      >
        {course && (
          <>
            <h2 className={course.groups_are_individuals  ? 'mt-5' : undefined}>{course?.name}</h2>
            {!course.groups_are_individuals && Array.isArray(course.group_clusters) && course.group_clusters?.length > 1 ? <p>Please select a session to join</p> : null}
            {alreadyInCourse ? (
              <>
                <Alert variant="info">You are already part of this course.</Alert>
                <a href={routes.elnCloud} className="btn btn-primary">Go to ELN<ArrowRight/></a>
              </>
            ) : (
              <SelectStudentGroupToJoin course={course} userIdToJoin={user.id}/>
            )}
          </>
        )}
      </ShareableLinkPage>
    );
  }

  if (loadingUser) {
    return (
      <Pages.Centred width="sm">
        <span className="d-flex align-items-center gap-2 justify-content-center">
          <Loading/>
          <span>Loading logged-in user...</span>
        </span>
      </Pages.Centred>
    );
  }

  // If no user is logged in, redirect to login page with redirect
  // param, so they are redirected back here.
  return <RedirectPage to={routes.loginWithRedirect(thisPageUrl)}/>;
}
